import { useState } from 'react';
import type { Address } from 'viem';
import { useBalance, useConnection, useReadContract, useReadContracts } from 'wagmi';
import { bondDepositoryAbi } from '@syntra/abi';
import { ActionButton } from '../components/tx/ActionButton.tsx';
import { CountUp } from '../components/ui/motion.tsx';
import {
  AmountInput,
  Button,
  Card,
  Countdown,
  Notice,
  Pill,
  Progress,
  Rows,
  SectionHead,
  Segmented,
  StatTile,
} from '../components/ui/primitives.tsx';
import { RingGauge } from '../components/ui/viz.tsx';
import { DEPLOYMENT as D } from '../config/env.ts';
import { useChainNow } from '../hooks/useChainClock.tsx';
import { usePosition } from '../hooks/usePosition.ts';
import { usePriceGuard } from '../hooks/usePriceGuard.ts';
import { PLAN_IDS, useProtocol } from '../hooks/useProtocol.ts';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import { bondLimit, E18 } from '../lib/bondLimits.ts';
import { fmtBps, fmtDuration, fmtToken, fmtUsd, parseAmount, toNum, toInput, fmtBalance } from '../lib/format.ts';
import { Coin, type Token } from '../components/ui/coins.tsx';
import { JoinFirst } from '../components/tx/JoinFirst.tsx';

const BPS = 10_000n;

export interface MarketMeta {
  id: number;
  asset: Address;
  symbol: string;
  icon: string;
  coin: Token;
  native: boolean;
}

export const MARKETS: MarketMeta[] = [
  { id: D.bnbMarket, asset: D.wbnb, symbol: 'BNB', icon: '/token-bnb.svg', coin: 'BNB', native: true },
  { id: D.usdtMarket, asset: D.usdt, symbol: 'USDT', icon: '/token-usdt.svg', coin: 'USDT', native: false },
  { id: D.lpMarket, asset: D.syntraUsdtPair, symbol: 'SYN-USDT LP', icon: '/token-lp.svg', coin: 'LP', native: false },
];

/** The slippage guard the contract checks: never accept less than `payout × (1 − slippage)`. */
export function minPayout(payout: bigint, slippageBps: bigint): bigint {
  return (payout * (BPS - slippageBps)) / BPS;
}

function useMarkets() {
  const q = useReadContracts({
    allowFailure: true,
    query: { refetchInterval: 15_000 },
    contracts: [
      ...MARKETS.flatMap((m) => [
        { address: D.bonds, abi: bondDepositoryAbi, functionName: 'market', args: [BigInt(m.id)] } as const,
        { address: D.bonds, abi: bondDepositoryAbi, functionName: 'quote', args: [BigInt(m.id), 0n] } as const,
        { address: D.bonds, abi: bondDepositoryAbi, functionName: 'availableCapacity', args: [BigInt(m.id)] } as const,
      ]),
      { address: D.bonds, abi: bondDepositoryAbi, functionName: 'totalOutstanding' } as const,
    ],
  });
  const r = q.data;
  const v = <T,>(i: number) => (r?.[i]?.status === 'success' ? (r[i].result as T) : undefined);
  const markets = MARKETS.map((m, i) => {
    const market = v<readonly [{ capacityPerPeriod: bigint; enabled: boolean; maxPayout: bigint; roiMinBps: number; roiMaxBps: number }, unknown]>(i * 3);
    const quote = v<{ roiBps: bigint; bondPriceUsd: bigint; discountBps: bigint; marketPriceUsd: bigint }>(i * 3 + 1);
    return { ...m, config: market?.[0], quote, available: v<bigint>(i * 3 + 2) };
  });
  return { markets, outstanding: v<bigint>(MARKETS.length * 3), isLoading: q.isLoading };
}

/** The oracle's manipulation guard as the contract applies it on every bond: spot within `maxSpotDeviationBps` of the TWAP. */
export function Bond() {
  const { t } = useI18n();
  const { data: p } = useProtocol();
  const { markets, outstanding, isLoading } = useMarkets();
  const [selected, setSelected] = useState(D.usdtMarket);
  const open = markets.filter((m) => m.config?.enabled).length;
  const market = markets.find((m) => m.id === selected) ?? markets[0];
  return (
    <>
      <SectionHead eyebrow={t('bond.eyebrow')} title={t('bond.title')} />
      <div className="grid tiles">
        <StatTile label={t('bond.price')} value={fmtUsd(p.price)} loading={isLoading} coin="SYN" />
        <StatTile label={t('bond.treasury')} value={fmtUsd(p.treasuryUsd)} loading={isLoading} />
        <StatTile label={t('bond.open')} value={t('bond.openOf', { n: open, m: markets.length })} loading={isLoading} />
        <StatTile label={t('bond.outstanding')} value={`${fmtToken(outstanding, 2)} SYN`} sub={t('bond.outstandingSub')} loading={isLoading} coin="SYN" />
      </div>

      <Card title={t('bond.markets')}>
        <table className="table stack">
          <thead>
            <tr>
              <th>{t('bond.asset')}</th>
              <th>{t('bond.bondPrice')}</th>
              <th>{t('bond.roi')}</th>
              <th>{t('bond.discount')}</th>
              <th>{t('bond.capacity')}</th>
              <th>{t('bond.vesting')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {markets.map((m) => {
              const cap = m.config?.capacityPerPeriod ?? 0n;
              const share = cap > 0n && m.available !== undefined ? Number((m.available * 10_000n) / cap) / 10_000 : 0;
              return (
                <tr key={m.id} data-testid={`market-${m.symbol}`}>
                  <td data-label={t('bond.asset')}>
                    <span className="token-chip" style={{ border: 0, background: 'none', paddingLeft: 0 }}>
                      <Coin token={m.coin} size={26} motion="hover" />
                      {m.symbol}
                    </span>
                  </td>
                  <td data-label={t('bond.bondPrice')}>{fmtUsd(m.quote?.bondPriceUsd)}</td>
                  <td data-label={t('bond.roi')} className="pos">
                    {m.quote ? fmtBps(m.quote.roiBps) : '—'}
                  </td>
                  <td data-label={t('bond.discount')}>{m.quote ? fmtBps(m.quote.discountBps) : '—'}</td>
                  <td data-label={t('bond.capacity')} style={{ minWidth: 140 }}>
                    <div className="field" style={{ gap: 4 }}>
                      <Progress value={share} />
                      <span className="muted" style={{ fontSize: 12 }}>
                        {fmtToken(m.available, 0)} SYN
                      </span>
                    </div>
                  </td>
                  <td data-label={t('bond.vesting')}>{t('bond.days7')}</td>
                  <td>
                    {m.config?.enabled ? (
                      <Button size="sm" variant={m.id === selected ? 'primary' : 'secondary'} onClick={() => setSelected(m.id)}>
                        {t('bond.select')}
                      </Button>
                    ) : (
                      <Pill tone="muted">{t('bond.closed')}</Pill>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
          {t('bond.roiExplain')}
        </p>
      </Card>

      <div className="grid two form-layout">
        <BondForm market={market} />
        <div className="grid fill-last">
          <MarketInsight market={market} />
          <Positions />
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        {t('common.notAdvice')}
      </p>
    </>
  );
}

type Market = ReturnType<typeof useMarkets>['markets'][number];

/** The selected market at a glance: ROI as a glowing ring against the market's ROI ceiling, prices, and capacity left. */
function MarketInsight({ market }: { market: Market }) {
  const { t } = useI18n();
  const q = market.quote;
  const cfg = market.config;
  const roi = q ? Number(q.roiBps) / 10_000 : undefined;
  const cap = cfg?.capacityPerPeriod ?? 0n;
  const share = cap > 0n && market.available !== undefined ? Number((market.available * 10_000n) / cap) / 10_000 : 0;
  return (
    <Card className="market-insight">
      <div className="card-head">
        <h3>
          <Coin token={market.coin} size={24} />
          {t('bond.insight', { asset: market.symbol })}
        </h3>
        {cfg ? <Pill tone={cfg.enabled ? 'green' : 'muted'}>{cfg.enabled ? t('bond.openNow') : t('bond.closed')}</Pill> : null}
      </div>
      <div className="insight-body">
        <RingGauge key={market.id} fraction={q && cfg?.roiMaxBps ? Number(q.roiBps) / cfg.roiMaxBps : 0} size={168} stroke={14} label={t('bond.roi')}>
          <span className="v num">
            <CountUp value={roi !== undefined ? roi * 100 : undefined} format={(v) => `${v.toFixed(2)}%`} />
          </span>
          <span className="l">{t('bond.roi')}</span>
        </RingGauge>
        <div className="insight-stats">
          <div className="bal-stat">
            <span className="k">{t('bond.bondPrice')}</span>
            <span className="v num">{fmtUsd(q?.bondPriceUsd)}</span>
          </div>
          <div className="bal-stat">
            <span className="k">{t('bond.marketPrice')}</span>
            <span className="v num">{fmtUsd(q?.marketPriceUsd)}</span>
          </div>
          <div className="bal-stat">
            <span className="k">{t('bond.discount')}</span>
            <span className="v num gradient-text">{q ? fmtBps(q.discountBps) : '—'}</span>
          </div>
        </div>
      </div>
      <div className="insight-cap">
        <div className="row">
          <span>{t('bond.capLeft')}</span>
          <span className="num">
            <b>{fmtToken(market.available, 0)} SYN</b> · {Math.round(share * 100)}%
          </span>
        </div>
        <Progress key={market.id} value={share} />
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '12px 0 0' }}>
        {cfg ? t('bond.roiBand', { min: fmtBps(BigInt(cfg.roiMinBps)), max: fmtBps(BigInt(cfg.roiMaxBps)) }) : ' '}
      </p>
    </Card>
  );
}

function BondForm({ market }: { market: Market }) {
  const { t } = useI18n();
  const { address } = useConnection();
  const { data: me } = usePosition(address);
  const bnb = useBalance({ address, query: { enabled: Boolean(address) } });
  const [input, setInput] = useState('');
  const [slippage, setSlippage] = useState<'50' | '100' | '300'>('100');
  const now = useChainNow(60_000);
  const amount = parseAmount(input);
  const balance = market.native ? bnb.data?.value : market.symbol === 'USDT' ? me.usdt : me.lp;
  const quote = useReadContract({
    address: D.bonds,
    abi: bondDepositoryAbi,
    functionName: 'quote',
    args: [BigInt(market.id), amount ?? 0n],
    query: { enabled: Boolean(amount && amount > 0n), refetchInterval: 10_000 },
  });
  // the USD value of one unit of the asset, so the per-bond limit can be shown in what the user pays
  const unit = useReadContract({
    address: D.bonds,
    abi: bondDepositoryAbi,
    functionName: 'quote',
    args: [BigInt(market.id), E18],
    query: { refetchInterval: 15_000 },
  });
  const { spot, twap, guard, reopensIn } = usePriceGuard();
  const q = quote.data;
  const min = q ? minPayout(q.payout, BigInt(slippage)) : 0n;
  const limit = bondLimit(market.config?.maxPayout, market.available, unit.data?.bondPriceUsd, unit.data?.valueUsd);
  const limitText = limit ? `${fmtToken(limit.amount, 2)} ${market.symbol}` : '';
  const overLimit = q && limit && q.payout > limit.payout;
  const blocked =
    !market.config?.enabled
      ? t('bond.closed')
      : guard?.paused
        ? t('bond.paused')
        : limit && limit.payout === 0n
          ? t('bond.soldOut')
          : amount === null || amount === 0n
            ? t('common.enterAmount')
            : balance !== undefined && amount > balance
              ? t('common.insufficient', { token: market.symbol })
              : overLimit
                ? t(limit.byCapacity ? 'bond.overCap' : 'bond.overMax', { amount: limitText })
                : null;
  return (
    <Card title={t('bond.form', { asset: market.symbol })} highlight>
      <div className="field" style={{ gap: 14 }}>
        <AmountInput
          id="bond-amount"
          label={t('bond.pay')}
          value={input}
          onChange={setInput}
          token={market.symbol}
          icon={market.icon}
          balance={fmtBalance(balance)}
          onMax={
            balance !== undefined
              ? () => {
                  const spendable = market.native ? (balance > 10n ** 16n ? balance - 10n ** 16n : 0n) : balance;
                  setInput(toInput(limit && limit.amount < spendable ? limit.amount : spendable));
                }
              : undefined
          }
          hint={market.native ? 'Keeps 0.01 BNB for gas' : undefined}
        />
        <Rows
          items={[
            [t('bond.value'), fmtUsd(q?.valueUsd)],
            [t('bond.receive'), <b key="r">{q ? `${fmtToken(q.payout)} SYN` : '—'}</b>],
            [t('bond.roi'), q ? <span key="roi" className="pos">{fmtBps(q.roiBps)}</span> : '—'],
            [t('bond.bondPrice'), fmtUsd(q?.bondPriceUsd)],
            [t('bond.unlock'), new Date((now + 7 * 86_400) * 1000).toLocaleString()],
            [t('bond.minReceive'), q ? `${fmtToken(min)} SYN` : '—'],
            [
              t(limit?.byCapacity ? 'bond.leftNow' : 'bond.maxPer'),
              limit ? <span key="lim">{t('bond.limitVal', { payout: fmtToken(limit.payout, 2), amount: limitText })}</span> : '—',
            ],
          ]}
        />
        {guard?.paused && spot !== undefined && twap !== undefined ? (
          <Notice tone="warn">
            {/* the title carries its own trailing space where the language uses one */}
            <b>{t('bond.pausedTitle')}</b>
            {t('bond.pausedWhy', {
              // four decimals, so the two prices visibly differ by the percentage shown
              spot: `$${toNum(spot).toFixed(4)}`,
              twap: `$${toNum(twap).toFixed(4)}`,
              dev: fmtBps(BigInt(guard.devBps)),
              max: `${guard.maxBps / 100}%`,
            })}
            {reopensIn !== undefined ? <span data-testid="bond-reopens"> {t('bond.reopensIn', { time: fmtDuration(reopensIn) })}</span> : null}
          </Notice>
        ) : null}
        <div className="row" style={{ alignItems: 'center' }}>
          <span className="soft" style={{ fontSize: 13 }}>
            {t('bond.slippage')}
          </span>
          <Segmented<'50' | '100' | '300'>
            label={t('bond.slippage')}
            value={slippage}
            onChange={setSlippage}
            options={[
              { value: '50', label: '0.5%' },
              { value: '100', label: '1%' },
              { value: '300', label: '3%' },
            ]}
          />
        </div>
        <Notice>{t('bond.feeNote')}</Notice>
        <ActionButton
          label={t('bond.cta')}
          blocked={blocked}
          approve={!market.native && amount ? { token: market.asset, spender: D.bonds, amount, symbol: market.symbol } : undefined}
          request={
            amount && q
              ? {
                  address: D.bonds,
                  abi: bondDepositoryAbi,
                  functionName: 'deposit',
                  args: [BigInt(market.id), amount, min],
                  value: market.native ? amount : undefined,
                }
              : null
          }
          onDone={() => setInput('')}
        />
      </div>
    </Card>
  );
}

function Positions() {
  const { t } = useI18n();
  const { address } = useConnection();
  const { data: me } = usePosition(address);
  const ids = me.bondIds ?? [];
  const now = useChainNow(5000);
  const [plan, setPlan] = useState(1); // claim & stake into the 30-day lock unless the user picks another plan
  const q = useReadContracts({
    allowFailure: false,
    query: { enabled: ids.length > 0, refetchInterval: 15_000 },
    contracts: ids.map((id) => ({ address: D.bonds, abi: bondDepositoryAbi, functionName: 'position', args: [id] }) as const),
  });
  // oldest first: `n` counts this wallet's own bonds from 1; `id` is the protocol-wide position id the contract uses
  const rows = (q.data ?? []).map((pos, i) => ({ id: ids[i], n: i + 1, ...pos }));
  const claimable = rows.filter((r) => !r.claimed && Number(r.unlockAt) <= now);
  const claimableTotal = claimable.reduce((s, r) => s + r.payout, 0n);
  const symbolOf = (marketId: number) => MARKETS.find((m) => m.id === marketId)?.symbol ?? `#${marketId}`;
  return (
    <Card title={t('bond.positions')}>
      {!address ? (
        <p className="soft">{t('common.connectFirst')}</p>
      ) : rows.length === 0 ? (
        <p className="soft">{t('bond.none')}</p>
      ) : (
        <div className="field" style={{ gap: 14 }}>
          <table className="table stack">
            <thead>
              <tr>
                <th>#</th>
                <th>{t('bond.market')}</th>
                <th>{t('bond.payout')}</th>
                <th>{t('bond.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const ready = !r.claimed && Number(r.unlockAt) <= now;
                return (
                  <tr key={String(r.id)} data-testid={`bond-${r.id}`}>
                    <td data-label="#" title={t('bond.chainId', { id: String(r.id) })}>
                      {r.n}
                    </td>
                    <td data-label={t('bond.market')}>{symbolOf(r.marketId)}</td>
                    <td data-label={t('bond.payout')}>{fmtToken(r.payout)} SYN</td>
                    <td data-label={t('bond.status')}>
                      {r.claimed ? (
                        <Pill tone="muted">{t('bond.claimed')}</Pill>
                      ) : ready ? (
                        <Pill tone="green">{t('bond.claimable')}</Pill>
                      ) : (
                        <Pill tone="amber">
                          {t('bond.vestingStatus')} · <Countdown to={Number(r.unlockAt)} />
                        </Pill>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {claimable.length > 0 ? (
            <>
              <b>{t('bond.claimableTotal', { amount: fmtToken(claimableTotal) })}</b>
              <ActionButton
                variant="secondary"
                label={t('bond.claimAll', { n: claimable.length })}
                request={{ address: D.bonds, abi: bondDepositoryAbi, functionName: 'claim', args: [claimable.map((r) => r.id)] }}
              />
              <div className="row" style={{ alignItems: 'center' }}>
                <span className="soft" style={{ fontSize: 13 }}>
                  {t('bond.into')}
                </span>
                <select className="text-input" aria-label={t('bond.into')} value={plan} onChange={(e) => setPlan(Number(e.target.value))} style={{ width: 'auto' }}>
                  {PLAN_IDS.map((id) => (
                    <option key={id} value={id}>
                      {t(`plan.${id}` as MessageKey)}
                    </option>
                  ))}
                </select>
              </div>
              <JoinFirst>
                <ActionButton
                  label={t('bond.claimStake', { n: claimable.length })}
                  request={{
                    address: D.bonds,
                    abi: bondDepositoryAbi,
                    functionName: 'claimAndStake',
                    args: [claimable.map((r) => r.id), plan],
                  }}
                />
              </JoinFirst>
            </>
          ) : null}
        </div>
      )}
    </Card>
  );
}

